import { useMemo } from 'react';
import { validateAgreementTermLength } from '@/common/utils/agreementDateValidation';
import type { MissingFieldsData } from '@/state/navigation';
import type { BuyerAgreementFormData } from '../components/BuyerAgreementForm';
import { useBuyerAgreementData } from './useBuyerAgreementData';

interface FieldRule {
  name: keyof BuyerAgreementFormData;
  label: string;
}

const REQUIRED_FIELDS: FieldRule[] = [
  { name: 'startDate', label: 'Start Date' },
  { name: 'endDate', label: 'End Date' },
  { name: 'commissionType', label: 'Commission Type' },
  { name: 'commissionValue', label: 'Commission' },
];

export function useBuyerAgreementMissingFields(clientGroupId: string) {
  const { agentState, clientGroup, clientEntityTypes, isLoading } =
    useBuyerAgreementData(clientGroupId);

  const missingFields = useMemo(() => {
    if (!clientGroup) return [];

    const missing: MissingFieldsData[] = [];

    for (const rule of REQUIRED_FIELDS) {
      const value = clientGroup[rule.name];
      const isEmpty = value === undefined || value === null || value === '';

      if (isEmpty) {
        missing.push({
          section: 'Buyer Agreement',
          label: rule.label,
          name: rule.name,
          index: 0,
          isAddable: false,
          cardId: 'agreementTerms',
        });
      }
    }

    // State-specific term length validation
    const { startDateError, endDateError } = validateAgreementTermLength(
      clientGroup.startDate,
      clientGroup.endDate,
      agentState,
      clientEntityTypes,
    );

    if (startDateError && !missing.some((m) => m.name === 'startDate')) {
      missing.push({
        section: 'Buyer Agreement',
        label: 'Start Date',
        name: 'startDate',
        index: 0,
        isAddable: false,
        cardId: 'agreementTerms',
      });
    }

    if (endDateError && !missing.some((m) => m.name === 'endDate')) {
      missing.push({
        section: 'Buyer Agreement',
        label: 'End Date',
        name: 'endDate',
        index: 0,
        isAddable: false,
        cardId: 'agreementTerms',
      });
    }

    return missing;
  }, [agentState, clientGroup, clientEntityTypes]);

  return {
    missingAgreementFields: missingFields,
    clientGroup,
    loading: isLoading,
  };
}
